import React, { useCallback, useContext, useEffect, useState } from "react";
import { makeStyles, styled } from "@mui/styles";
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  Grid,
  Paper,
  List,
  ListItemText,
  Button,
  Stack,
  ListItem,
  Hidden,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

//Date related importes
import dayjs from "dayjs";

//... Imports icons
import ReplyIcon from "@mui/icons-material/Reply";

//... service importe
import { EmailNotificationService } from "../../services/notification.Service";

//... Context provider imports
import AppDataContext from "../../common/appContext";

//... Shared components imports
import RouteConstants from "../../common/RouteConstants";
import Loader from "../../sharedComponents/loader/loader";

const useStyles = makeStyles((theme) => ({
  appbarBackground: {
    backgroundColor: "#fff",
    color: "#1d428a",
  },
  title: {
    flexGrow: 1,
  },
  centerContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column", // Align content vertically
  },
  paperContainer: {
    padding: "20px",
    width: "80%", // Adjust as needed
    margin: "10px",
  },
  inputContainer: {
    width: "48%",
    paddingLeft: "1rem",
  },
  buttonContainer: {
    marginTop: "8px",
    marginLeft: "16px",
  },
}));

const StyledBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  margin: "auto",
});

const StyledList = styled(List)({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const StyledListItem = styled(ListItem)({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  padding: "8px",
  borderRadius: "4px",
  backgroundColor: "#f0f0f0",
});

const ViewEmailNotification = () => {
  const classes = useStyles();
  const params = useParams();
  const navigate = useNavigate();

  //... Context
  const { user } = useContext(AppDataContext);

  //... Loader data holders
  const [loading, setLoading] = useState(false);

  //... Notification Details data holders.
  const [notificationDetails, setNotificationDetails] = useState({
    notificationID: 0,
    fromName: "",
    recipientsTxt: "",
    recipientsDLOptions: "",
    customNames: "",
    subjectLine: "",
    emailBodyNotes: "",
    scheduleDate: null,
    attachments: [],
    emailStatus: 0,
    createdBy: null,
    createdDate: null,
    modifiedBy: null,
    modifiedDate: null,
  });

  useEffect(() => {
    if (params?.notificationID > 0) {
      getNotificationDetailsByID(params.notificationID);
    }
  }, [params.notificationID]);

  //Handlers
  const handleBackNavigation = () => {
    navigate(`${RouteConstants.MANAGE_EMAIL_NOTIFICATION}`);
  };

  const getNotificationDetailsByID = async (notificationID) => {
    try {
      setLoading(true);
      const notificationDetails =
        await EmailNotificationService.getNotificationDetailsByID(
          notificationID
        );

      if (notificationDetails.data) {
        const {
          id,
          dL_Options,
          dL_OptionsText,
          additional_To,
          fromName,
          subject,
          body,
          scheduledDate,
          attachmentJSON,
          createdDate,
          modifiedBy,
          modifiedDate,
          emailStatus,
          createdBy,
        } = notificationDetails.data;

        const attachedFiles = attachmentJSON ? JSON.parse(attachmentJSON) : [];
        const fileNames = attachedFiles.map((file) => {
          const extension = file.sourceFileName.substring(
            file.sourceFileName.lastIndexOf(".") + 1
          );
          return {
            displayFileName: file.sourceFileName.replace(`.${extension}`, ""),
            fileName: file.guidFileName,
            finalFileName: file.sourceFileName,
            isAlreadyUploaded: true,
          };
        });
        setNotificationDetails({
          notificationID: id || params?.notificationID,
          fromName: fromName || "",
          recipientsTxt: dL_OptionsText || null,
          recipientsDLOptions: "",
          customNames: additional_To || null,
          subjectLine: subject || "",
          emailBodyNotes: convertHtmlTextToPlainTxt(body) || "",
          scheduleDate: scheduledDate ? dayjs(scheduledDate) : null,
          attachments: fileNames || [],
          emailStatus: emailStatus || 0,
          createdBy: createdBy || null,
          createdDate: createdDate ? dayjs(createdDate) : null,
          modifiedBy: modifiedBy || null,
          modifiedDate: modifiedDate ? dayjs(modifiedDate) : null,
        });
        // setSelectedRecipient(
        //   (dL_Options?.split(",") || []).map((value) => parseInt(value, 10))
        // );
        // setSelectedCustomNames(additional_To || null);
        // setSSelectedFromName(fromName || "");
        // setSSelectedSubjectName(subject || "");
        // setSelectedEmailBody(convertHtmlTextToPlainTxt(body) || "");
        // setAttachments(fileNames || []);
        // setStartDateTime(scheduledDate ? dayjs(scheduledDate) : null);
        setLoading(false);
      }
    } catch (error) {
      console.log("Error while fetching notification details: ", error);
      setLoading(false);
    }
  };

  //..Utility to validate value
  const validateValue = useCallback((value) => {
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }

    if (typeof value === "number") {
      return value > 0 ? value : 0;
    }

    return "--"; // Default return when the value doesn't meet the conditions
  }, []);

  //..Utility to convert html text to  plain text.
  const convertHtmlTextToPlainTxt = (htmlText) => {
    // Create a DOMParser
    const parser = new DOMParser();

    // Parse the HTML text
    const doc = parser.parseFromString(htmlText, "text/html");

    // Extract the text content
    const plainText = doc.body.textContent || "";
    return plainText;
  };

  //... Utility to render title component
  const RenderTitle = ({ title, content }) => (
    <Grid item lg={12}>
      <Box className={classes.inputContainer}>
        <Typography
          variant="subtitle1"
          component="label"
          gutterBottom
          fontWeight={800}
          color="primary" // Added color prop to make the title stand out
        >
          {`${title} :`}
        </Typography>
      </Box>
      <Box
        sx={{
          margin: "8px 16px",
          padding: "6px 10px",
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div
          style={{
            overflowWrap: "break-word",
          }}
        >
          <Typography variant="body1" gutterBottom>
            {content}
          </Typography>
        </div>
      </Box>
    </Grid>
  );

  return (
    <>
      <Loader isLoading={loading} />
      <AppBar position="static" className={classes.appbarBackground}>
        <Toolbar>
          <Typography variant="h6" className={classes.title}>
            View Email Notification
          </Typography>
        </Toolbar>
      </AppBar>
      <Container maxWidth={false} className={classes.centerContainer}>
        <Paper elevation={3} className={classes.paperContainer}>
          <Grid container spacing={3} justify="space-around">
            <Grid item lg={6} md={6} sm={6} xs={12}>
              <Grid container spacing={3} justify="space-around">
                <RenderTitle
                  title="Recipients"
                  content={validateValue(notificationDetails?.recipientsTxt)}
                />
                <RenderTitle
                  title="From Name"
                  content={validateValue(notificationDetails?.fromName)}
                />
                <RenderTitle
                  title="Schedule Date"
                  content={
                    notificationDetails?.scheduleDate
                      ? dayjs(notificationDetails?.scheduleDate).format(
                          "DD-MM-YYYY h:mm A"
                        )
                      : "--"
                  }
                />
                {/* Attachments Section */}
                <RenderTitle
                  title="Attachments"
                  content={
                    notificationDetails?.attachments?.length > 0 ? (
                      <StyledBox>
                        <StyledList>
                          {notificationDetails?.attachments?.map(
                            (file, index) => (
                              <StyledListItem key={index}>
                                <ListItemText primary={file.finalFileName} />
                              </StyledListItem>
                            )
                          )}
                        </StyledList>
                      </StyledBox>
                    ) : (
                      <StyledListItem>None</StyledListItem>
                    )
                  }
                />
              </Grid>
            </Grid>
            <Grid item lg={6} md={6} sm={6} xs={12}>
              <Grid container spacing={3} justify="space-around">
                <RenderTitle
                  title="Additional Email-Id's"
                  content={validateValue(notificationDetails?.customNames)}
                />

                <RenderTitle
                  title="Subject Line"
                  content={validateValue(notificationDetails?.subjectLine)}
                />
                <RenderTitle
                  title="Created Date"
                  content={
                    notificationDetails?.createdDate
                      ? dayjs(notificationDetails?.createdDate).format(
                          "DD-MM-YYYY h:mm A"
                        )
                      : "--"
                  }
                />
                <RenderTitle
                  title="Email Body"
                  content={validateValue(notificationDetails?.emailBodyNotes)}
                />
              </Grid>
            </Grid>

            {/* Back Button */}
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Box className={classes.buttonContainer}>
                <Stack direction="row" spacing={2}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => handleBackNavigation()}
                  >
                    <ReplyIcon style={{ marginRight: "5px" }} />
                    <Hidden smDown> Back</Hidden>
                  </Button>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Container>
      ;
    </>
  );
};

export default ViewEmailNotification;
